/**
 * 画面フローのルート(docs/02_game_design.md §1)。
 *
 * React Router は使わず Zustand の `screen` で切り替える(画面数が少なく、
 * ゲームなので URL 復元の要件も無い)。
 */

import { lazy, Suspense, useEffect } from 'react';
import { useAuthStore } from './store/authStore';
import { useGameStore } from './store/gameStore';
import { AccountScreen } from './ui/AccountScreen';
import { AvatarScreen } from './ui/AvatarScreen';
import { GachaScreen } from './ui/GachaScreen';
import { ModeSelectScreen } from './ui/ModeSelectScreen';
import { MyPageScreen } from './ui/MyPageScreen';
import { PlayScreen } from './ui/PlayScreen';
import { ResultScreen } from './ui/ResultScreen';
import { SoundToggle } from './ui/SoundToggle';
import { StageSelectScreen } from './ui/StageSelectScreen';
import { StagePreparationDialog } from './ui/StagePreparationDialog';
import { TitleScreen } from './ui/TitleScreen';
import { appServices } from './services/appServices';

/**
 * 思考力パズル(docs/11_thinking_module.md)。
 *
 * `@paltyping/puzzle`・盤面コンポーネント・パズル用 CSS はこの動的 import の
 * 向こう側にしかない = **タイピングの初回ロードには載らない**(docs/11 §4)。
 * Phaser 背景(`ui/PhaserBackground`)と同じ方式。
 */
const PuzzleMode = lazy(() => import('./ui/puzzle/PuzzleMode'));

/**
 * まなびモード(5教科。docs/10_paltown_platform.md §2)。
 *
 * `@paltyping/quiz`・問題バンクの取得・まなび用 CSS はこの動的 import の
 * 向こう側にしかない = **タイピングの初回ロードには載らない**。パズルと同じ方式。
 */
const LearnMode = lazy(() => import('./ui/learn/LearnMode'));

/**
 * パルの ステータス画面(docs/14_characters.md §2)。
 *
 * パズル・まなびの進捗を読むために `@paltyping/puzzle` / `@paltyping/quiz` を
 * 触るので、パズル・まなびと同じく**遅延読み込み**にしてタイピングの初回ロードから外す。
 */
const PalStatusScreen = lazy(() =>
  import('./ui/PalStatusScreen').then((module) => ({ default: module.PalStatusScreen })),
);

export function App() {
  const screen = useGameStore((state) => state.screen);
  const init = useGameStore((state) => state.init);
  const stageAccessStatus = useGameStore((state) => state.stageAccessStatus);
  const bundleStatus = useGameStore((state) => state.bundleStatus);
  // ログイン状態の購読開始。ログインしていなくてもゲームは何も変わらない。
  const initAuth = useAuthStore((state) => state.init);

  useEffect(() => {
    if (!appServices.recoveryBlocked) void init();
  }, [init]);

  useEffect(() => {
    if (!appServices.recoveryBlocked) return initAuth();
    return undefined;
  }, [initAuth]);

  if (appServices.recoveryBlocked) {
    return (
      <main className="recovery-blocked" role="alert">
        <h1>ふっきゅうの じゅんびが できませんでした</h1>
        <p>このたんまつの きろくを ほぞんできないため、はじめられません。</p>
        <p>あきようりょうを かくにんしてから、ページを よみこみなおしてね。</p>
      </main>
    );
  }

  const preparingStage =
    stageAccessStatus === 'loading' || (screen === 'play' && bundleStatus === 'loading');

  return (
    <div className={`app screen-${screen}`}>
      <div data-app-content>
        {screen === 'title' ? <TitleScreen /> : null}
        {screen === 'mode' ? <ModeSelectScreen /> : null}
        {screen === 'stageSelect' ? <StageSelectScreen /> : null}
        {screen === 'play' ? <PlayScreen /> : null}
        {screen === 'result' ? <ResultScreen /> : null}
        {screen === 'gacha' ? <GachaScreen /> : null}
        {screen === 'avatar' ? <AvatarScreen /> : null}
        {screen === 'myPage' ? <MyPageScreen /> : null}
        {screen === 'account' ? <AccountScreen /> : null}
        {screen === 'pal' ? (
          <Suspense
            fallback={
              <div className="screen pal-loading">
                <p className="loading-text">よみこみちゅう…</p>
              </div>
            }
          >
            <PalStatusScreen />
          </Suspense>
        ) : null}
        {screen === 'puzzle' ? (
          <Suspense
            fallback={
              <div className="screen puzzle-loading">
                <p className="loading-text">よみこみちゅう…</p>
              </div>
            }
          >
            <PuzzleMode />
          </Suspense>
        ) : null}
        {screen === 'learn' ? (
          <Suspense
            fallback={
              <div className="screen learn-loading">
                <p className="loading-text">よみこみちゅう…</p>
              </div>
            }
          >
            <LearnMode />
          </Suspense>
        ) : null}
        {screen === 'play' ? null : <SoundToggle />}
      </div>
      {preparingStage ? <StagePreparationDialog /> : null}
    </div>
  );
}
